import { Matrix4, Vector3 } from "three";

import ExtrudedLoopAnnotationData from "./ExtrudedLoopAnnotationData";
import SceneObjectTypeEnum from "./SceneObjectTypeEnum";
import Placement3d from "../Placement3d";
import { vector3ToJson, vector3FromJson } from "../../services/bimVisualizerApi/primitivesSerialization";
import { UUID } from "../../utils/typeDefinitions";
import Constants from "../../constants";
import { mmFromSceneUnits, sceneUnitsFromMm } from "../../utils/scaler";
import { applyOnlyRotation } from "../../utils/transformUtils";
import areaAnnotationDataSchema from "../../services/bimVisualizerApi/schema/document/sceneObject/areaAnnotationDataSchema";

class AreaAnnotationData extends ExtrudedLoopAnnotationData {

    points: Vector3[];

    constructor(    placement: Placement3d,
                    points: Vector3[],
                    depth: number = 0,
                    extrudedDirection: Vector3 = new Vector3(0, 0, 1),
                    fontSize: number = AreaAnnotationData.DEFAULT_FONT_SIZE,
                    showAreaText: boolean = true,
                    showVolume: boolean = false,
                    uuid?: UUID,
                    parentId?: UUID) {
        super(SceneObjectTypeEnum.AreaAnnotation, placement, depth, extrudedDirection, fontSize, showAreaText, showVolume, uuid, parentId);
        this.points = points;
    }

    clone() : this {
        const clone = new AreaAnnotationData(
                                        this.placement.clone() as Placement3d,
                                        this.points.map(p => p.clone()),
                                        this.depth,
                                        this.extrudedDirection.clone(),
                                        this.fontSize,
                                        this.showAreaText,
                                        this.showVolume,
                                        this.uuid, this.parentId) as this;

        this.copyBaseData(clone);
        return clone;
    }

    protected getLoopPoints3d(): Vector3[] {
        return this.points;
    }

    // Enclosed area of the (possibly non-axis-aligned) planar polygon defined by `points`, via
    // Newell's method — this only needs the points' relative offsets, so it's unaffected by them
    // being expressed relative to placement.location rather than in world coordinates, and it does
    // not require the points to actually lie in the plane implied by placement.refDirection/axis
    // (see AreaAnnotationData.test.ts). Scene units are already meters (see utils/scaler.ts), so
    // the result is directly in m².
    computeEnclosedArea(): number {
        if (this.points.length < 3) return 0;

        let nx = 0, ny = 0, nz = 0;
        for (let i = 0; i < this.points.length; i++) {
            const current = this.points[i];
            const next = this.points[(i + 1) % this.points.length];
            nx += (current.y - next.y) * (current.z + next.z);
            ny += (current.z - next.z) * (current.x + next.x);
            nz += (current.x - next.x) * (current.y + next.y);
        }

        return 0.5 * Math.sqrt(nx * nx + ny * ny + nz * nz);
    }

    applyMatrix4(matrix: Matrix4): void {
        this.applyMatrix4Base(matrix);
        // Points are offsets relative to placement.location, expressed in the same (world) axes as the
        // placement itself, so they must rotate along with it to stay consistent with refDirection/axis.
        this.points.forEach(p => applyOnlyRotation(p, matrix));
    }

    toJson(): any {
        return {
            ...this.toJsonBase(),
            points: this.points.map(p => vector3ToJson(p, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits)),
        };
    }

    static fromJson(json: any): AreaAnnotationData {

        const parsedData = areaAnnotationDataSchema.parse(json);

        return new AreaAnnotationData(
            new Placement3d(
                vector3FromJson(parsedData.placement.location, sceneUnitsFromMm),
                vector3FromJson(parsedData.placement.refDirection),
                vector3FromJson(parsedData.placement.axis)
            ),
            parsedData.points.map(p => vector3FromJson(p, sceneUnitsFromMm)),
            sceneUnitsFromMm(parsedData.depth),
            vector3FromJson(parsedData.extrudedDirection),
            sceneUnitsFromMm(parsedData.fontSize ?? mmFromSceneUnits(AreaAnnotationData.DEFAULT_FONT_SIZE)),
            parsedData.showAreaText ?? true,
            parsedData.showVolume ?? false,
            parsedData.uuid,
            parsedData.parentId ?? undefined
        );
    }

}

export default AreaAnnotationData;
